import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toggleSidebar, toggleDarkMode } from '../../store/slices/uiSlice';
import { setCurrentChannel } from '../../store/slices/chatSlice';
import Sidebar from '../chat/Sidebar';
import { MenuIcon } from '../chat/SidebarIcons';
import SavedMessagesPanel from './SavedMessagesPanel';

/**
 * SavedMessagesPage - Main page component for saved messages functionality
 * Includes sidebar and saved messages panel
 */
const SavedMessagesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sidebarOpen, darkMode } = useSelector((state) => state.ui);
  const { currentChannel } = useSelector((state) => state.chat);

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  const handleToggleDarkMode = () => {
    dispatch(toggleDarkMode());
  };

  const handleChannelSelect = (channel) => {
    // Set the current channel and navigate to chat page
    if (channel) {
      dispatch(setCurrentChannel(channel));
      // Small delay to ensure Redux state is updated before navigation
      setTimeout(() => {
        navigate('/chat');
      }, 50);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      {/* Backdrop when sidebar is open on mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={handleToggleSidebar}
          onKeyDown={(e) => e.key === 'Escape' && handleToggleSidebar()}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
        />
      )}

      {/* Sidebar: fully collapsed (0 width) on mobile when closed; overlay when open on mobile */}
      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          sidebarOpen
            ? 'w-80 fixed lg:relative left-0 top-0 h-full z-40 lg:z-auto shadow-xl lg:shadow-none'
            : 'w-0 lg:w-16'
        }`}
      >        <Sidebar
          currentChannel={currentChannel}
          onChannelSelect={handleChannelSelect}
          onLogout={() => {
            // Handle logout if needed
            window.location.href = '/login';
          }}
          onToggleSidebar={handleToggleSidebar}
          onToggleDarkMode={handleToggleDarkMode}
          sidebarOpen={sidebarOpen}
          darkMode={darkMode}
        />
      </div>

      {/* Saved Messages Panel */}
      <div className="flex-1 flex flex-col relative min-w-0">
        {/* Mobile: open sidebar button when sidebar is closed */}
        {!sidebarOpen && (
          <button
            type="button"
            className="lg:hidden absolute left-2 top-2 p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            onClick={handleToggleSidebar}
            aria-label="Open menu"
          >
            <MenuIcon className="w-6 h-6" />
          </button>
        )}
        <SavedMessagesPanel />
      </div>
    </div>
  );
};

export default SavedMessagesPage;
